import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { HistorySyncInput, useWatchSync } from '../hooks/useWatchSync';
import { loadProfileStorage, migrateAllProfileRecords, profileStorageKey } from '../utils/profileStorage';
import '../components/ExitModal/ExitModal.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HistoryItem {
  /** TMDB id as string */
  id: string;
  type: 'movie' | 'series';
  name: string;
  posterUrl?: string;
  backdropUrl?: string;
  /** For movies: vodStreamId. For series: seriesStreamId (Xtream series_id) */
  streamId: string;
  /** For series: stream id of the last watched episode (used for progress bar) */
  lastEpisodeStreamId?: string;
  /** When true, this item was watched from a kids context */
  isKids?: boolean;
  /** ms timestamp of last watch event */
  updatedAt: number;
}

interface WatchHistoryContextType {
  addToHistory: (item: HistoryItem) => void;
  removeFromHistory: (id: string, type: 'movie' | 'series') => void;
  getHistory: () => HistoryItem[];
  getMovieHistory: () => HistoryItem[];
  getSeriesHistory: () => HistoryItem[];
  isInHistory: (id: string, type: 'movie' | 'series') => boolean;
  clearAllLocal: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WatchHistoryContext = createContext<WatchHistoryContextType>({
  addToHistory: () => {},
  removeFromHistory: () => {},
  getHistory: () => [],
  getMovieHistory: () => [],
  getSeriesHistory: () => [],
  isInHistory: () => false,
  clearAllLocal: () => {},
});

const STORAGE_KEY_PREFIX = 'ott_player_history:';

const toKey = (id: string, type: 'movie' | 'series') => `${type}:${id}`;

// ─── Provider ─────────────────────────────────────────────────────────────────

export const WatchHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authData } = useAuth();
  const { t } = useTranslation();
  const { canSync, fetchRemote, pushHistory, removeHistory } = useWatchSync();
  const username = authData?.user?.username;
  const storageKey = profileStorageKey(STORAGE_KEY_PREFIX, authData?.profileId, username);
  const legacyStorageKey = username ? `${STORAGE_KEY_PREFIX}${username}` : null;

  const [data, setData] = useState<Record<string, HistoryItem>>(() => {
    if (!storageKey) return {};
    try {
      const raw = authData?.profileId
        ? migrateAllProfileRecords<HistoryItem>(storageKey, STORAGE_KEY_PREFIX)
        : loadProfileStorage(storageKey, legacyStorageKey);
      return raw ? (JSON.parse(raw) as Record<string, HistoryItem>) : {};
    } catch {
      return {};
    }
  });

  // Confirmation dialog state for multi-device delete
  const [pendingDelete, setPendingDelete] = useState<{ id: string; type: 'movie' | 'series' } | null>(null);

  // Reload when user changes (login / logout)
  useEffect(() => {
    if (!storageKey) { setData({}); return; }
    try {
      const raw = authData?.profileId
        ? migrateAllProfileRecords<HistoryItem>(storageKey, STORAGE_KEY_PREFIX)
        : loadProfileStorage(storageKey, legacyStorageKey);
      setData(raw ? (JSON.parse(raw) as Record<string, HistoryItem>) : {});
    } catch {
      setData({});
    }
  }, [authData?.profileId, legacyStorageKey, storageKey]);

  // On mount / account change: fetch remote history and REPLACE local (backend is source of truth)
  const syncedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = storageKey;
    if (!canSync || !key || syncedRef.current === key) return;
    syncedRef.current = key;
    fetchRemote().then((remote) => {
      if (!remote) return; // network failure — keep local
      const next: Record<string, HistoryItem> = {};
      for (const item of remote.history) {
        const mapKey = toKey(item.contentId, item.contentType as 'movie' | 'series');
        next[mapKey] = {
          id:                  item.contentId,
          type:                item.contentType as 'movie' | 'series',
          name:                item.name,
          posterUrl:           item.posterUrl ?? undefined,
          backdropUrl:         item.backdropUrl ?? undefined,
          streamId:            item.streamId,
          lastEpisodeStreamId: item.lastEpisodeStreamId ?? undefined,
          isKids:              item.isKids,
          updatedAt:           item.updatedAt,
        };
      }
      setData(next);
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore */ }
    }).catch(() => { /* network error — local data stays */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSync, storageKey]);

  const persist = useCallback(
    (next: Record<string, HistoryItem>) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // localStorage full — fail silently
      }
    },
    [storageKey],
  );

  const addToHistory = useCallback(
    (item: HistoryItem) => {
      setData((prev) => {
        const key = toKey(item.id, item.type);
        const entry = { ...item, updatedAt: Date.now() };
        const next = { ...prev, [key]: entry };
        persist(next);
        // Push to backend (debounced 5s)
        const syncItem: HistorySyncInput = {
          contentId: item.id,
          contentType: item.type,
          name: item.name,
          posterUrl: item.posterUrl,
          backdropUrl: item.backdropUrl,
          streamId: item.streamId,
          lastEpisodeStreamId: item.lastEpisodeStreamId,
          isKids: item.isKids,
        };
        pushHistory([syncItem]);
        return next;
      });
    },
    [persist, pushHistory],
  );

  const removeFromHistory = useCallback(
    (id: string, type: 'movie' | 'series') => {
      // Show confirmation dialog — actual delete happens in confirmDelete
      setPendingDelete({ id, type });
    },
    [],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const { id, type } = pendingDelete;
    setPendingDelete(null);
    setData((prev) => {
      const key = toKey(id, type);
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      persist(next);
      return next;
    });
    // Propagate delete to backend (awaited so it completes)
    removeHistory(id, type);
  }, [pendingDelete, persist, removeHistory]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const getHistory = useCallback(
    (): HistoryItem[] =>
      Object.values(data).sort((a, b) => b.updatedAt - a.updatedAt),
    [data],
  );

  const getMovieHistory = useCallback(
    (): HistoryItem[] =>
      Object.values(data)
        .filter((item) => item.type === 'movie')
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [data],
  );

  const getSeriesHistory = useCallback(
    (): HistoryItem[] =>
      Object.values(data)
        .filter((item) => item.type === 'series')
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [data],
  );

  const isInHistory = useCallback(
    (id: string, type: 'movie' | 'series'): boolean => !!data[toKey(id, type)],
    [data],
  );

  const clearAllLocal = useCallback(() => {
    setData({});
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify({})); } catch { /* ignore */ }
    }
  }, [storageKey]);

  const value = useMemo(
    () => ({ addToHistory, removeFromHistory, getHistory, getMovieHistory, getSeriesHistory, isInHistory, clearAllLocal }),
    [addToHistory, removeFromHistory, getHistory, getMovieHistory, getSeriesHistory, isInHistory, clearAllLocal],
  );

  return (
    <WatchHistoryContext.Provider value={value}>
      {children}
      {pendingDelete && (
        <div className="exitmodal__overlay" onClick={cancelDelete}>
          <div className="exitmodal__box" onClick={(e) => e.stopPropagation()}>
            <p className="exitmodal__title">{t('watchSync.removeAllDevicesTitle')}</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0, textAlign: 'center', maxWidth: 320 }}>
              {t('watchSync.removeAllDevicesDesc')}
            </p>
            <div className="exitmodal__actions">
              <button className="exitmodal__btn exitmodal__btn--yes" onClick={confirmDelete} autoFocus>
                {t('common.confirm')}
              </button>
              <button className="exitmodal__btn exitmodal__btn--no" onClick={cancelDelete}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </WatchHistoryContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useWatchHistory = () => useContext(WatchHistoryContext);
